/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, MouseEvent } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React TS examples components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Auth context
import { useAuth } from "context/auth/authContext";

// Avatar utilities
import { stringAvatar } from "utils/avatar";

// Declaring prop types for DashboardNavbar
interface Props {
  absolute?: boolean;
  light?: boolean;
  isMini?: boolean;
}

function DashboardNavbar({ absolute, light, isMini }: Props): JSX.Element {
  const [navbarType, setNavbarType] = useState<
    "fixed" | "absolute" | "relative" | "static" | "sticky"
  >();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState<HTMLElement | null>(null);
  const [openProfileMenu, setOpenProfileMenu] = useState<HTMLElement | null>(null);
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();
  
  const auth = useAuth();
  const { user, isAuthenticated } = auth;
  const [avatarProps, setAvatarProps] = useState<Record<string, any> | null>(null);
  
  useEffect(() => {
    if (user && user.firstName && user.lastName) {
      setAvatarProps(stringAvatar(`${user.firstName} ${user.lastName}`));
    }
  }, [user]);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => {
    // Force update the configurator state
    setOpenConfigurator(dispatch, true);
  };
  const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(null);
  const handleOpenProfileMenu = (event: MouseEvent<HTMLButtonElement>) => setOpenProfileMenu(event.currentTarget);
  const handleCloseProfileMenu = () => setOpenProfileMenu(null);
  
  const handleLogout = () => {
    auth.logout();
    handleCloseProfileMenu();
    navigate('/authentication/sign-in/basic');
  };
  
  const handleAccountSettings = () => {
    handleCloseProfileMenu();
    navigate('/pages/account/settings');
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>inventory_2</Icon>} title="4 parts near reorder point" />
      <NotificationItem icon={<Icon>local_shipping</Icon>} title="Shipment SHP-2026-0043 in transit" />
      <NotificationItem icon={<Icon>rule</Icon>} title="2 cycle-count variances to review" />
    </Menu>
  );
  
  // Render the profile menu
  const renderProfileMenu = () => (
    <Menu
      anchorEl={openProfileMenu}
      id="account-menu"
      open={Boolean(openProfileMenu)}
      onClose={handleCloseProfileMenu}
      onClick={handleCloseProfileMenu}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      sx={{
        mt: 1.5,
        '& .MuiPaper-root': {
          borderRadius: 2,
          minWidth: 180,
          boxShadow: '0px 0px 35px 0px rgba(0,0,0,0.1)',
          '& .MuiMenuItem-root': {
            px: 1.5,
            py: 1,
          },
        },
      }}
    >
      <MDBox px={1.5} py={1} display="flex" alignItems="center">
        <MDTypography variant="button" fontWeight="medium" color="text">
          {user ? `${user.firstName} ${user.lastName}` : 'User'}
        </MDTypography>
      </MDBox>
      <Divider sx={{ my: 1 }} />
      <MenuItem onClick={handleAccountSettings}>
        <ListItemIcon>
          <Icon fontSize="small">person</Icon>
        </ListItemIcon>
        Account settings
      </MenuItem>
      <Divider sx={{ my: 1 }} />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <Icon fontSize="small">logout</Icon>
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette, functions }: any) => {
    const { dark, white, text } = palette;
    const { rgba } = functions;

    return {
      color: () => {
        let colorValue = light || darkMode ? white.main : dark.main;

        if (transparentNavbar && !light) {
          colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
        }

        return colorValue;
      },
    };
  };

  // Use an immediate invocation to get a simple object for the sx prop
  const iconColor = (theme: any) => ({
    color: iconsStyle(theme).color(),
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={navbarContainer}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
          <IconButton sx={navbarDesktopMenu} onClick={handleMiniSidenav} size="small" disableRipple>
            <Icon fontSize="medium" sx={iconColor}>
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </IconButton>
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox pr={1}>
              <MDInput label="Search here" />
            </MDBox>
            <MDBox color={light ? "white" : "inherit"}>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconColor} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconColor}>settings</Icon>
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                onClick={handleOpenMenu}
              >
                <MDBadge badgeContent={9} color="error" size="xs" circular>
                  <Icon sx={iconColor}>notifications</Icon>
                </MDBadge>
              </IconButton>
              {isAuthenticated && (
                <IconButton
                  size="small"
                  edge="end"
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleOpenProfileMenu}
                >
                  {user && avatarProps ? (
                    <Avatar 
                      {...avatarProps} 
                      sx={{ 
                        width: 32, 
                        height: 32,
                        fontSize: '1rem',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
                      }} 
                    />
                  ) : (
                    <Icon sx={iconColor}>account_circle</Icon>
                  )}
                </IconButton>
              )}
              {renderMenu()}
              {renderProfileMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Declaring default props for DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

export default DashboardNavbar;
